import React from 'react';
import {
  Box,
  Container,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EmailOutlined,
  Facebook,
  Instagram,
  LinkedIn,
  LocationOnOutlined,
  MusicNoteRounded,
  PhoneOutlined,
  YouTube,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoSrc from '../images/LogoVector.webp';

const socialLinks = [
  {
    name: 'LinkedIn',
    href:
      process.env.REACT_APP_LINKEDIN_URL ||
      'https://www.linkedin.com/company/smtlink-solutions',
    icon: LinkedIn,
    hoverColor: '#0A66C2',
  },
  {
    name: 'Facebook',
    href:
      process.env.REACT_APP_FACEBOOK_URL ||
      'https://www.facebook.com/profile.php?id=61585664267011',
    icon: Facebook,
    hoverColor: '#1877F2',
  },
  {
    name: 'Instagram',
    href:
      process.env.REACT_APP_INSTAGRAM_URL ||
      'https://www.instagram.com/smtlink.solutions/',
    icon: Instagram,
    hoverColor: '#E1306C',
  },
  {
    name: 'TikTok',
    href:
      process.env.REACT_APP_TIKTOK_URL ||
      'https://www.tiktok.com/@smtlink.solutions',
    icon: MusicNoteRounded,
    hoverColor: '#FE2C55',
  },
  {
    name: 'YouTube',
    href:
      process.env.REACT_APP_YOUTUBE_URL ||
      'https://www.youtube.com/@SMTLINKSolutions',
    icon: YouTube,
    hoverColor: '#FF0033',
  },
];

const ContactItem = ({ icon: Icon, children, href }) => {
  const content = (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'rgba(67, 145, 224, 0.14)',
          border: '1px solid rgba(118, 178, 239, 0.2)',
          borderRadius: 1,
          color: '#72B6F5',
          display: 'flex',
          flex: '0 0 auto',
          height: 32,
          justifyContent: 'center',
          mt: 0.15,
          width: 32,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography
        component="span"
        sx={{
          color: 'rgba(255,255,255,0.74)',
          fontSize: '0.86rem',
          lineHeight: 1.65,
          overflowWrap: 'anywhere',
          transition: 'color 180ms ease',
        }}
      >
        {children}
      </Typography>
    </Stack>
  );

  if (!href) return content;

  return (
    <Link
      href={href}
      underline="none"
      sx={{
        display: 'block',
        '&:hover span, &:focus-visible span': { color: '#FFFFFF' },
        '&:focus-visible': { borderRadius: 1, outline: '2px solid #72B6F5' },
      }}
    >
      {content}
    </Link>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const navigationLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.services'), to: '/services' },
    { label: t('nav.about'), to: '/about' },
    { label: t('nav.contact'), to: '/contact' },
    { label: t('nav.forum'), to: '/forum' },
  ];

  return (
    <Box
      component="footer"
      id="site-footer"
      sx={{
        bgcolor: '#0B2038',
        borderTop: '4px solid #2F80D1',
        color: '#FFFFFF',
        position: 'relative',
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 6.5 } }}>
        <Grid container spacing={{ xs: 4, md: 5 }} justifyContent="space-between">
          <Grid item xs={12} md={5} lg={4}>
            <Box
              component={RouterLink}
              to="/"
              aria-label={t('nav.home')}
              sx={{
                display: 'inline-flex',
                mb: 2,
                '&:focus-visible': {
                  borderRadius: 1,
                  outline: '2px solid #72B6F5',
                  outlineOffset: 4,
                },
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt="SMTLINK Solutions"
                loading="lazy"
                sx={{
                  filter: 'brightness(0) invert(1)',
                  height: { xs: 48, sm: 54 },
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  width: 'auto',
                }}
              />
            </Box>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.72)',
                fontSize: '0.94rem',
                lineHeight: 1.75,
                maxWidth: 440,
              }}
            >
              {t('footer.company.description')}
            </Typography>

            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '0.78rem',
                fontWeight: 700,
                mt: 3,
                textTransform: 'uppercase',
              }}
            >
              {t('footer.social.title')}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
              {socialLinks.map(({ name, href, icon: Icon, hoverColor }) => (
                <Tooltip title={name} arrow key={name}>
                  <IconButton
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={name}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      borderRadius: 1,
                      color: '#FFFFFF',
                      height: 42,
                      transition:
                        'background-color 180ms ease, border-color 180ms ease, transform 180ms ease',
                      width: 42,
                      '&:hover': {
                        bgcolor: hoverColor,
                        borderColor: hoverColor,
                        transform: 'translateY(-2px)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #72B6F5',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 21 }} />
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={7} lg={7}>
            <Grid container spacing={{ xs: 4, sm: 3, lg: 5 }}>
              <Grid item xs={6} sm={4} md={3}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {t('footer.navigation.title')}
                </Typography>
                <Stack component="nav" aria-label={t('footer.navigation.title')} spacing={1.15}>
                  {navigationLinks.map(({ label, to }) => (
                    <Link
                      component={RouterLink}
                      to={to}
                      underline="none"
                      key={to}
                      sx={{
                        alignItems: 'center',
                        color: 'rgba(255,255,255,0.7)',
                        display: 'inline-flex',
                        fontSize: '0.86rem',
                        lineHeight: 1.5,
                        transition: 'color 180ms ease, transform 180ms ease',
                        width: 'fit-content',
                        '&::before': {
                          bgcolor: '#55A7ED',
                          content: '""',
                          height: 1,
                          mr: 1,
                          transition: 'width 180ms ease',
                          width: 8,
                        },
                        '&:hover, &:focus-visible': {
                          color: '#FFFFFF',
                          transform: 'translateX(3px)',
                        },
                        '&:hover::before, &:focus-visible::before': { width: 14 },
                        '&:focus-visible': {
                          borderRadius: 0.5,
                          outline: '2px solid #72B6F5',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {label}
                    </Link>
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={8} md={5}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {t('footer.locations.title')}
                </Typography>
                <Stack spacing={2}>
                  <ContactItem icon={LocationOnOutlined}>
                    {t('footer.locations.queretaro')}
                  </ContactItem>
                  <ContactItem icon={LocationOnOutlined}>
                    {t('footer.locations.newYork')}
                  </ContactItem>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={7} md={4}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {t('footer.contact.title')}
                </Typography>
                <Stack spacing={1.65}>
                  <ContactItem icon={EmailOutlined} href="mailto:Sales@smtlinkss.com">
                    Sales@smtlinkss.com
                  </ContactItem>
                  <ContactItem icon={PhoneOutlined} href="tel:+524427845691">
                    +52 442 784 5691
                  </ContactItem>
                  <ContactItem icon={PhoneOutlined} href="tel:+12624977859">
                    +1 262 497 7859
                  </ContactItem>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <Box
        sx={{
          bgcolor: '#071524',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.25, sm: 3 },
            justifyContent: 'space-between',
            pb: { xs: 9, sm: 2.25 },
            pt: 2.25,
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.58)',
              fontSize: '0.76rem',
              lineHeight: 1.6,
            }}
          >
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>

          <Link
            component={RouterLink}
            to="/terms-and-conditions"
            underline="none"
            sx={{
              color: 'rgba(255,255,255,0.68)',
              fontSize: '0.76rem',
              fontWeight: 600,
              transition: 'color 180ms ease',
              '&:hover, &:focus-visible': { color: '#FFFFFF' },
              '&:focus-visible': {
                borderRadius: 0.5,
                outline: '2px solid #72B6F5',
                outlineOffset: 3,
              },
            }}
          >
            {t('footer.legal.terms')}
          </Link>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
